import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { crmLeadApi, crmSearchApi } from '../../api/crmApi';
import { getAllGoods } from '../../api/goodsApi';
import AppDropdown from '../../components/AppDropdown';
import { INDIAN_STATES } from '../../data/indiaStates';
import { LeadStatusBadge, PriorityBadge, SourceBadge } from './components/LeadStatusBadge';
import { ActivityTimeline } from './components/ActivityTimeline';
import { CallModal } from './components/CallModal';
import { FollowupModal } from './components/FollowupModal';
import UserSearchInput from './components/UserSearchInput';
import './CRM.css';
import './CRM_LEAD_DETAIL.css';

const STAGES = ['New', 'Contacted', 'Interested', 'Not Interested', 'Qualified', 'Requirement', 'Quotation', 'Negotiation', 'Won', 'Lost', 'Converted'];
const SOURCES = ['IndiaMART', 'Manual', 'Website', 'Referral', 'Other'];

const fallbackLead = {
  id: 1, leadId: 'LEAD-1001', customerName: 'Ankit Mehta', company: 'Mehta Oxygen Pvt. Ltd.', mobile: '9876543210',
  altMobile: '', email: 'ankit@mehtaoxygen.in', city: 'Ahmedabad', state: 'Gujarat', address: 'Plot 22, Industrial Estate',
  requirement: '50 Industrial Gas Cylinders', product: 'Oxygen Cylinder', quantity: 50, unitPrice: 1650,
  source: 'IndiaMART', indiamartLeadId: 'IM-8801', assignedTo: 'Rahul Sharma', status: 'Qualified', priority: 'High',
  expectedValue: 82500, expectedClosingDate: '2026-10-20', lastContact: '2026-10-02', nextFollowup: '2026-10-04',
  createdAt: '2026-09-28', notes: 'Bulk order for new plant, 5% discount requested.', ctsCustomerId: null,
};
const fallbackActivities = [
  { id: 1, type: 'Call', title: 'Call completed', desc: 'Outcome: Interested — quoted ₹1,650/unit', actor: 'Rahul Sharma', createdAt: '2026-10-03T09:15:00' },
  { id: 2, type: 'WhatsApp', title: 'Brochure sent', desc: 'Product catalogue shared on WhatsApp', actor: 'Rahul Sharma', createdAt: '2026-10-02T15:40:00' },
  { id: 3, type: 'Lead', title: 'Lead created from IndiaMART', desc: 'IM-8801 — Oxygen Cylinders x 50', actor: 'System', createdAt: '2026-09-28T10:00:00' },
];
const fallbackCalls = [
  { id: 1, outcome: 'Interested', durationSeconds: 272, calledAt: '2026-10-03T09:15:00', notes: 'Interested in 50 oxygen cylinders', direction: 'Outbound' },
];
const fallbackFollowups = [
  { id: 1, type: 'Call', scheduledAt: '2026-10-04T10:30:00', priority: 'High', status: 'Pending', notes: 'Discuss quotation' },
];
const fallbackOpps = [
  { id: 1, opportunityId: 'OPP-1041', expectedValue: 82500, stage: 'Negotiation', probability: 70, expectedCloseDate: '2026-10-20' },
];

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDT = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const dur = (s) => { if (!s && s !== 0) return '—'; const m = Math.floor(s / 60); return m ? `${m}m ${s % 60}s` : `${s}s`; };

const goodsLabel = (g) => g?.name || g?.productName || g?.goodsName || g?.description || g?.productCode || '';
const normalizeGoods = (res) => {
  const list = Array.isArray(res?.data) ? res.data : res?.data?.data || res?.data?.goods || [];
  const rows = Array.isArray(list) ? list : list.content || [];
  return rows.filter((g) => goodsLabel(g));
};
const useGoods = () => {
  const [goods, setGoods] = useState([]);
  const [goodsLoading, setGoodsLoading] = useState(false);
  const timerRef = useRef(null);
  const seqRef = useRef(0);
  const prefetchedRef = useRef([]);

  useEffect(() => () => { clearTimeout(timerRef.current); }, []);

  // Pre-fetch all products once on mount so the dropdown has options
  // available to browse immediately, without requiring a search first.
  useEffect(() => {
    const seq = ++seqRef.current;
    setGoodsLoading(true);
    getAllGoods()
      .then((r) => {
        if (seq !== seqRef.current) return;
        const list = normalizeGoods(r);
        prefetchedRef.current = list;
        setGoods(list);
      })
      .catch(() => { if (seq === seqRef.current) setGoods([]); })
      .finally(() => { if (seq === seqRef.current) setGoodsLoading(false); });
  }, []);

  const searchGoods = (q) => {
    clearTimeout(timerRef.current);
    const keyword = String(q || '').trim();
    if (keyword.length < 2) {
      // Restore the pre-fetched list when the search box is cleared.
      setGoods(prefetchedRef.current);
      setGoodsLoading(false);
      return;
    }

    const seq = ++seqRef.current;
    setGoodsLoading(true);
    timerRef.current = setTimeout(() => {
      getAllGoods({ search: keyword })
        .then((r) => {
          if (seq !== seqRef.current) return;
          const list = normalizeGoods(r)
            .filter((g) => goodsLabel(g).toLowerCase().includes(keyword.toLowerCase()));
          setGoods(list);
        })
        .catch(() => { if (seq === seqRef.current) setGoods([]); })
        .finally(() => { if (seq === seqRef.current) setGoodsLoading(false); });
    }, 350);
  };

  return { goods, goodsLoading, searchGoods };
};

/* =================== MULTI-PRODUCT ROWS =================== */
const makeProductRow = (product = '', quantity = '', unitPrice = '') => ({
  uid: `${Date.now()}-${Math.round(Math.random() * 1e6)}`,
  product,
  quantity,
  unitPrice,
});

const seedProductRows = (source = {}) => {
  const names = String(source.product || '').split(',').map((s) => s.trim()).filter(Boolean);
  const list = names.length ? names : [''];
  return list.map((name, i) => makeProductRow(
    name,
    i === 0 && source.quantity !== undefined && source.quantity !== null ? source.quantity : '',
    i === 0 && source.unitPrice !== undefined && source.unitPrice !== null ? source.unitPrice : '',
  ));
};

const productFields = (rows) => {
  const filled = rows.filter((r) => r.product || r.quantity || r.unitPrice);
  const totalQty = filled.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  const firstPrice = filled.find((r) => r.unitPrice !== '' && r.unitPrice !== undefined);
  return {
    product: filled.map((r) => r.product).filter(Boolean).join(', '),
    quantity: totalQty || '',
    unitPrice: firstPrice ? firstPrice.unitPrice : '',
  };
};

const productOptionsFor = (goods, current) => {
  const options = goods.map((g) => ({ value: goodsLabel(g), label: goodsLabel(g) }));
  if (current && !options.some((o) => o.value === current)) options.unshift({ value: current, label: current });
  return options;
};

const ProductRows = ({ rows, setRows, goods, loading, searchGoods }) => {
  const update = (uid, patch) => setRows(rows.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  const addRow = () => setRows([...rows, makeProductRow()]);
  const removeRow = (uid) => { if (rows.length > 1) setRows(rows.filter((r) => r.uid !== uid)); };

  const filled = rows.filter((r) => r.product);
  const totalQty = rows.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);

  return (
    <div className="crm-product-block">
      <div className="crm-product-table">
        <div className="crm-product-head">
          <span>#</span>
          <span>Product</span>
          <span>Qty</span>
          <span>Unit Price (₹)</span>
          <span>Expected Value (₹)</span>
          <span />
        </div>

        {rows.map((row, idx) => (
          <div className="crm-product-row" key={row.uid}>
            <span className="crm-product-index">{idx + 1}</span>
            <div className="crm-product-cell crm-product-cell--product" data-label="Product">
              <AppDropdown
                value={row.product}
                options={productOptionsFor(goods, row.product)}
                onChange={(val) => update(row.uid, { product: val })}
                placeholder="Select Product..."
                searchable
                minSearchChars={2}
                onSearch={searchGoods}
                loadingSearch={loading}
                searchPlaceholder="Type to search product..."
                menuClassName="crm-dropdown-window"
              />
            </div>
            <div className="crm-product-cell crm-product-cell--qty" data-label="Quantity">
              <input type="number" min="0" placeholder="0" value={row.quantity}
                onChange={(e) => update(row.uid, { quantity: e.target.value })} />
            </div>
            <div className="crm-product-cell crm-product-cell--price" data-label="Unit Price (₹)">
              <input type="number" min="0" placeholder="0" value={row.unitPrice}
                onChange={(e) => update(row.uid, { unitPrice: e.target.value })} />
            </div>
            <div className="crm-product-cell crm-product-cell--expected" data-label="Expected Value (₹)">
              <span className="crm-product-expected" title="Quantity × Unit Price">
                ₹{((Number(row.quantity) || 0) * (Number(row.unitPrice) || 0)).toLocaleString('en-IN')}
              </span>
            </div>
            <div className="crm-product-cell crm-product-cell--delete">
              <button type="button" className="crm-product-delete" onClick={() => removeRow(row.uid)}
                disabled={rows.length === 1} aria-label="Remove product" title="Remove product">
                <Icon name="trash" size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="crm-product-footer">
        <button type="button" className="crm-btn crm-btn-sm crm-btn-outline" onClick={addRow}>
          <Icon name="plus" size={13} /> Add Product
        </button>
        <span className="crm-product-summary">
          {filled.length} product{filled.length === 1 ? '' : 's'} · Total qty {totalQty}
        </span>
      </div>
    </div>
  );
};

/* =================== STATE SELECT (India) =================== */
const STATE_OPTIONS = INDIAN_STATES.map((s) => ({ value: s, label: s }));

const StateSelect = ({ value, onChange }) => (
  <div className="crm-field">
    <label>State</label>
    <AppDropdown
      value={value || ''}
      options={STATE_OPTIONS}
      onChange={onChange}
      placeholder="Select State"
      searchable
      minSearchChars={2}
      searchPlaceholder="Type to search state..."
      menuWidth={240}
      menuClassName="crm-dropdown-window"
    />
  </div>
);

/* =================== GENERIC LABELLED DROPDOWN =================== */
const PRIORITIES = ['High', 'Medium', 'Low'];
const toOptions = (list) => (list || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));

const FieldSelect = ({ label, value, options, onChange, placeholder, req }) => (
  <div className="crm-field">
    <label>{label} {req && <span className="req">*</span>}</label>
    <AppDropdown
      value={value || ''}
      options={toOptions(options)}
      onChange={onChange}
      placeholder={placeholder || `Select ${label}`}
      menuWidth={240}
      menuClassName="crm-dropdown-window"
    />
  </div>
);

/* =================== INLINE SVG ICONS =================== */
const Icon = ({ name, size = 16, strokeWidth = 1.9, className = '' }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className,
    'aria-hidden': 'true',
    focusable: 'false',
  };

  const paths = {
    user: <><circle cx="12" cy="7" r="4" /><path d="M5.5 21a6.5 6.5 0 0 1 13 0" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    userCheck: <><circle cx="9" cy="7" r="4" /><path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" /><path d="m16 11 2 2 4-4" /></>,
    plus: <><path d="M12 5v14M5 12h14" /></>,
    arrowLeft: <><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></>,
    arrowDown: <><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></>,
    arrowUp: <><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></>,
    x: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
    xCircle: <><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6M9 9l6 6" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
    edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
    fileText: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M16 13H8M16 17H8M10 9H8" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
    save: <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8" /><path d="M7 3v5h8" /></>,
    phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.2 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L8 9.73a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 22 16.92z" /></>,
    message: <><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" /></>,
    mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>,
    calendar: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" /></>,
    dot: <><circle cx="12" cy="12" r="5" fill="currentColor" stroke="none" /></>,
    alert: <><path d="M10.3 3.3 2.4 17a2 2 0 0 0 1.73 3h15.74a2 2 0 0 0 1.73-3L13.7 3.3a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></>,
    check: <><path d="M20 6 9 17l-5-5" /></>,
    checkCircle: <><circle cx="12" cy="12" r="9" /><path d="m8 12 2.5 2.5L16 9" /></>,
    refresh: <><path d="M20 11a8 8 0 0 0-14.5-4.5L4 8" /><path d="M4 4v4h4" /><path d="M4 13a8 8 0 0 0 14.5 4.5L20 16" /><path d="M20 20v-4h-4" /></>,
    trash: <><path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M10 11v6M14 11v6" /></>,
  };

  return <svg {...common}>{paths[name] || paths.user}</svg>;
};

/* =================== CREATE MODE =================== */
const LeadCreate = ({ navigate }) => {
  const location = useLocation();
  const imLead = location.state?.indiaMartLead || null;
  const [form, setForm] = useState({
    customerName: imLead?.customerName || '', company: imLead?.company || '', mobile: imLead?.mobile || '',
    altMobile: '', email: imLead?.email || '', city: imLead?.city || '', state: '', address: '',
    requirement: imLead?.requirement || '', product: imLead?.product || '', quantity: '', unitPrice: '',
    source: imLead ? 'IndiaMART' : 'Manual', indiamartLeadId: imLead?.indiamartLeadId || '',
    assignedTo: '', status: 'New', priority: 'Medium', expectedValue: '', expectedClosingDate: '',
    nextFollowup: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [dupeCheck, setDupeCheck] = useState({ checked: false, dupes: [] });
  const { goods, goodsLoading, searchGoods } = useGoods();
  const [products, setProducts] = useState(() => [makeProductRow(imLead?.product || '')]);

  const MOBILE_FIELDS = ['mobile', 'altMobile'];
  const sanitizeMobile = (val) => String(val || '').replace(/\D/g, '').slice(0, 10);

  const set = (k) => (e) => {
    const raw = e && e.target ? e.target.value : e;
    const value = MOBILE_FIELDS.includes(k) ? sanitizeMobile(raw) : raw;
    setForm((p) => ({ ...p, [k]: value }));
  };

  const checkDuplicates = useCallback(async () => {
    if (!form.mobile && !form.indiamartLeadId) { setDupeCheck({ checked: true, dupes: [] }); return; }
    let dupes = [];
    try {
      const res = await crmSearchApi.searchLeads(form.indiamartLeadId || form.mobile);
      dupes = Array.isArray(res.data) ? res.data : res.data?.content || [];
    } catch {
      if (form.indiamartLeadId === 'IM-8801') dupes = [fallbackLead];
    }
    setDupeCheck({ checked: true, dupes });
  }, [form.mobile, form.indiamartLeadId]);

  const save = async () => {
    if (!form.customerName.trim()) { toast.error('Customer name is required.'); return; }
    if (!form.mobile.trim()) { toast.error('Mobile number is required.'); return; }
    if (!/^[6-9]\d{9}$/.test(form.mobile)) { toast.error('Enter a valid 10-digit mobile number.'); return; }
    if (form.altMobile && !/^[6-9]\d{9}$/.test(form.altMobile)) { toast.error('Enter a valid 10-digit alternate mobile number.'); return; }
    if (products.some((r) => r.quantity && !r.product)) { toast.error('Select a product for every row with a quantity.'); return; }
    const payload = { ...form, ...productFields(products) };
    setSaving(true);
    try {
      const res = await crmLeadApi.create(payload);
      toast.success(`Lead ${res.data?.leadId || ''} created.`);
      navigate('/crm/my-leads');
    } catch {
      toast.info('Saved locally (CRM API not reachable).');
      navigate('/crm/my-leads');
    } finally { setSaving(false); }
  };

  const field = (k, label, type = 'text', req = false) => {
    const isMobile = MOBILE_FIELDS.includes(k);
    return (
      <div className="crm-field">
        <label>{label} {req && <span className="req">*</span>}</label>
        <input
          type={type}
          value={form[k] || ''}
          onChange={set(k)}
          inputMode={isMobile ? 'numeric' : undefined}
          maxLength={isMobile ? 10 : undefined}
          pattern={isMobile ? '[0-9]{10}' : undefined}
          placeholder={isMobile ? '10-digit mobile number' : undefined}
        />
      </div>
    );
  };

  return (
    <div className="crm-page crm-lead-detail-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><Icon name="plus" size={20} strokeWidth={2.2} /></div>
          <div>
            <h1 className="crm-title">New Lead</h1>
            <p className="crm-subtitle">{imLead ? `From IndiaMART ${imLead.indiamartLeadId} — verify & complete` : 'Capture a new sales lead'}</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn crm-btn-ghost" onClick={() => navigate('/crm/my-leads')}>
            <Icon name="arrowLeft" size={14} /> Cancel
          </button>
          <button className="crm-btn crm-btn-outline" onClick={checkDuplicates}>
            <Icon name="search" size={14} /> Check Duplicates
          </button>
        </div>
      </div>

      {dupeCheck.checked && (
        <div className={`crm-banner ${dupeCheck.dupes.length ? 'warn' : 'ok'}`}>
          <div className="crm-banner-row">
            <Icon name={dupeCheck.dupes.length ? 'alert' : 'checkCircle'} size={15} />
            <span>
              {dupeCheck.dupes.length
                ? `Possible duplicate${dupeCheck.dupes.length > 1 ? 's' : ''} found: ${dupeCheck.dupes.map((d) => d.leadId).join(', ')}.`
                : 'No duplicates found for this mobile / IndiaMART ID.'}
            </span>
            {dupeCheck.dupes.length > 0 && (
              <button className="crm-btn crm-btn-sm crm-btn-outline"
                onClick={() => navigate(`/crm/leads/${dupeCheck.dupes[0].id}`)}>Open Existing</button>
            )}
          </div>
        </div>
      )}

      <div className="crm-panel-card">
        <div className="crm-panel-card-title">Customer</div>
        <div className="crm-form-grid">
          {field('customerName', 'Customer Name', 'text', true)}
          {field('company', 'Company')}
          {field('mobile', 'Mobile', 'tel', true)}
          {field('altMobile', 'Alternate Mobile')}
          {field('email', 'Email', 'email')}
          {field('city', 'City')}
          <StateSelect value={form.state} onChange={(val) => setForm((p) => ({ ...p, state: val }))} />
          <div className="crm-field crm-field-full">
            <label>Address</label>
            <input value={form.address} onChange={set('address')} />
          </div>
        </div>
      </div>

      <div className="crm-panel-card">
        <div className="crm-panel-card-title">Requirement</div>
        <div className="crm-form-grid">
          <div className="crm-field crm-field-full">
            <label>Requirement <span className="req">*</span></label>
            <input value={form.requirement} onChange={set('requirement')} placeholder="e.g. 50 Industrial Gas Cylinders" />
          </div>
          {field('expectedValue', 'Expected Value (₹)', 'number')}
          {field('expectedClosingDate', 'Expected Closing Date', 'date')}
        </div>
        <ProductRows rows={products} setRows={setProducts} goods={goods} loading={goodsLoading} searchGoods={searchGoods} />
      </div>

      <div className="crm-panel-card">
        <div className="crm-panel-card-title">Assignment & Status</div>
        <div className="crm-form-grid">
          <FieldSelect label="Source" value={form.source} options={SOURCES} onChange={set('source')} />
          {field('indiamartLeadId', 'IndiaMART Lead ID')}
          <UserSearchInput
            label="Assigned To"
            value={form.assignedTo}
            onChange={(val) => setForm((p) => ({ ...p, assignedTo: val }))}
          />
          <FieldSelect label="Status" value={form.status} options={STAGES} onChange={set('status')} />
          <FieldSelect label="Priority" value={form.priority} options={PRIORITIES} onChange={set('priority')} />
          {field('nextFollowup', 'Next Follow-up', 'date')}
          <div className="crm-field crm-field-full">
            <label>Notes</label>
            <textarea rows={3} value={form.notes} onChange={set('notes')} placeholder="Requirement details, budget, decision maker..." />
          </div>
        </div>
      </div>

      <div className="crm-form-footer">
        <button className="crm-btn crm-btn-primary" onClick={save} disabled={saving}>
          {saving ? <Icon name="refresh" size={14} className="crm-spin" /> : <Icon name="plus" size={14} />}
          {saving ? 'Saving...' : 'Create Lead'}
        </button>
      </div>
    </div>
  );
};

/* =================== DETAIL / EDIT MODE =================== */
const LeadDetail = ({ navigate, id }) => {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [activities, setActivities] = useState(fallbackActivities);
  const [calls, setCalls] = useState(fallbackCalls);
  const [followups, setFollowups] = useState(fallbackFollowups);
  const [opps] = useState(fallbackOpps);
  const [callModal, setCallModal] = useState(false);
  const [followupModal, setFollowupModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { goods, goodsLoading, searchGoods } = useGoods();
  const [products, setProducts] = useState(() => seedProductRows(fallbackLead));

  const fetchLead = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmLeadApi.getById(id);
      const data = res.data || fallbackLead;
      setLead(data); setForm(data); setProducts(seedProductRows(data));
    } catch {
      setLead(fallbackLead); setForm(fallbackLead); setProducts(seedProductRows(fallbackLead));
    } finally { setLoading(false); }
  }, [id]);

  const fetchRelated = useCallback(async () => {
    const [a, c, f] = await Promise.allSettled([
      crmLeadApi.getActivities(id), crmLeadApi.getCalls(id), crmLeadApi.getFollowups(id),
    ]);
    if (a.status === 'fulfilled') { const d = Array.isArray(a.value?.data) ? a.value.data : a.value?.data?.content; if (d?.length) setActivities(d); }
    if (c.status === 'fulfilled') { const d = Array.isArray(c.value?.data) ? c.value.data : c.value?.data?.content; if (d?.length) setCalls(d); }
    if (f.status === 'fulfilled') { const d = Array.isArray(f.value?.data) ? f.value.data : f.value?.data?.content; if (d?.length) setFollowups(d); }
  }, [id]);

  useEffect(() => { fetchLead(); fetchRelated(); }, [fetchLead, fetchRelated]);

  const save = async () => {
    if (products.some((r) => r.quantity && !r.product)) { toast.error('Select a product for every row with a quantity.'); return; }
    const payload = { ...form, ...productFields(products) };
    setSaving(true);
    try {
      await crmLeadApi.update(id, payload);
      setLead({ ...lead, ...payload }); setForm(payload);
      setEditing(false);
      toast.success('Lead updated.');
      navigate('/crm/my-leads');
    } catch { toast.error('Failed to update lead.'); }
    finally { setSaving(false); }
  };

  const setStatus = async (status) => {
    const prev = lead.status;
    setLead({ ...lead, status }); setForm({ ...form, status });
    try { await crmLeadApi.update(id, { ...form, status }); toast.success(`Status → ${status}`); }
    catch { toast.error('Failed to update status.'); setLead({ ...lead, status: prev }); }
  };

  const assign = async () => {
    const name = window.prompt('Assign to:', lead.assignedTo || '');
    if (!name) return;
    try {
      await crmLeadApi.assign(id, { salesperson: name });
      setLead({ ...lead, assignedTo: name }); setForm({ ...form, assignedTo: name });
      toast.success(`Assigned to ${name}.`);
    } catch { toast.error('Failed to assign.'); }
  };

  const convert = async () => {
    if (!window.confirm(`Convert "${lead.customerName}" to CTS Customer? Existing customers are matched by mobile/email.`)) return;
    try { await crmLeadApi.convert(id, { customerId: lead.ctsCustomerId || null }); } catch { /* continue */ }
    toast.success('Opening CTS Customer creation...');
    navigate('/dashboard/customers/new', { state: { fromCrm: true, leadId: id, lead } });
  };

  const createQuotation = () => navigate('/dashboard/estimates/new', { state: { crmLeadId: id, crmLead: lead } });
  const createOpportunity = async () => {
    const opp = { customerName: lead.customerName, company: lead.company, mobile: lead.mobile, email: lead.email, product: lead.product, quantity: lead.quantity, expectedValue: lead.expectedValue, leadId: id, source: lead.source, priority: lead.priority };
    try {
      const res = await crmLeadApi.createOpportunity(id, opp);
      toast.success('Opportunity created.');
      navigate(`/crm/opportunities/${res.data?.id || ''}`);
    } catch { navigate('/crm/opportunities/new', { state: { leadId: id, lead } }); }
  };

  const markLost = async () => {
    const reason = window.prompt('Why is this lead lost?', '');
    if (reason === null) return;
    try { await crmLeadApi.markLost(id, { reason }); } catch {}
    setLead({ ...lead, status: 'Lost' }); setForm({ ...form, status: 'Lost' });
    toast.success('Lead marked lost.');
  };

  if (loading) return <div className="crm-page crm-lead-detail-page"><div className="crm-loading">Loading lead...</div></div>;
  if (!lead) return null;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'activities', label: `Activities (${activities.length})` },
    { key: 'calls', label: `Calls (${calls.length})` },
    { key: 'followups', label: `Follow-ups (${followups.length})` },
    { key: 'opportunities', label: `Opportunities (${opps.length})` },
  ];

  const field = (k, label, type = 'text') => (
    <div className="crm-field">
      <label>{label}</label>
      <input type={type} value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
    </div>
  );

  return (
    <div className="crm-page crm-lead-detail-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><Icon name="user" size={20} strokeWidth={2} /></div>
          <div>
            <h1 className="crm-title">
              {lead.customerName} <span className="crm-title-id">· {lead.leadId}</span>
            </h1>
            <p className="crm-subtitle">{lead.company} · {lead.mobile} · {lead.city}</p>
          </div>
        </div>
        <div className="crm-header-actions">
            <button className="crm-btn crm-btn-ghost" onClick={() => navigate('/crm/my-leads')}>
              <Icon name="arrowLeft" size={14} /> Back
            </button>
            {editing ? (
              <>
                <button className="crm-btn crm-btn-ghost" onClick={() => setEditing(false)}>
                  <Icon name="x" size={14} /> Cancel
                </button>
                <button className="crm-btn crm-btn-primary" onClick={save} disabled={saving}>
                  <Icon name="save" size={14} /> {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <button className="crm-btn crm-btn-outline" onClick={() => setEditing(true)}>
                <Icon name="edit" size={14} /> Edit
              </button>
            )}
            <button className="crm-btn crm-btn-ghost" onClick={() => navigate('/crm/my-leads')}>
              <Icon name="arrowLeft" size={14} /> Back to My Leads
            </button>
          </div>
        </div>

      {!editing && (
        <div className="crm-action-bar">
          <div className="crm-action-bar-group">
            <button className="crm-btn crm-btn-outline" onClick={assign}>
              <Icon name="users" size={14} /> Assign
            </button>
            <button className="crm-btn crm-btn-outline" onClick={createQuotation}>
              <Icon name="fileText" size={14} /> Quotation
            </button>
            <button className="crm-btn crm-btn-outline" onClick={createOpportunity}>
              <Icon name="target" size={14} /> Opportunity
            </button>
          </div>
          <div className="crm-action-bar-group">
            {lead.status !== 'Lost' && (
              <button className="crm-btn crm-btn-danger" onClick={markLost}>
                <Icon name="xCircle" size={14} /> Mark Lost
              </button>
            )}
            <button className="crm-btn crm-btn-primary" onClick={convert}>
              <Icon name="userCheck" size={14} /> Convert to Customer
            </button>
          </div>
        </div>
      )}

      <div className="crm-detail-grid crm-lead-detail-grid">
        <div className="crm-panel-card">
          <div className="crm-panel-card-title">Lead Details</div>
          <div className="crm-info-row"><span>Status</span><strong><LeadStatusBadge status={lead.status} /></strong></div>
          <div className="crm-info-row"><span>Priority</span><strong><PriorityBadge priority={lead.priority} /></strong></div>
          <div className="crm-info-row"><span>Source</span><strong><SourceBadge source={lead.source} /></strong></div>
          <div className="crm-info-row"><span>IndiaMART ID</span><strong>{lead.indiamartLeadId || '—'}</strong></div>
          <div className="crm-info-row"><span>Assigned To</span><strong>{lead.assignedTo || '—'}</strong></div>
          <div className="crm-info-row"><span>Created</span><strong>{fmt(lead.createdAt)}</strong></div>
          <div className="crm-info-row"><span>Last Contact</span><strong>{fmt(lead.lastContact)}</strong></div>
          <div className="crm-info-row"><span>Next Follow-up</span><strong>{fmt(lead.nextFollowup)}</strong></div>
        </div>

        <div className="crm-panel-card">
          <div className="crm-panel-card-title">Contact</div>
          <div className="crm-info-row"><span>Mobile</span><strong>{lead.mobile}</strong></div>
          <div className="crm-info-row"><span>Alt Mobile</span><strong>{lead.altMobile || '—'}</strong></div>
          <div className="crm-info-row"><span>Email</span><strong>{lead.email || '—'}</strong></div>
          <div className="crm-info-row"><span>City</span><strong>{lead.city || '—'}</strong></div>
          <div className="crm-info-row"><span>CTS Customer</span>
            <strong>
              {lead.ctsCustomerId
                ? <span className="crm-linked"><Icon name="checkCircle" size={13} /> #{lead.ctsCustomerId}</span>
                : <span className="crm-unlinked">Not linked</span>}
            </strong>
          </div>
          <div className="crm-quick-actions">
            <a className="crm-btn crm-btn-sm crm-btn-outline" href={`tel:${lead.mobile}`}>
              <Icon name="phone" size={13} /> Call
            </a>
            <a className="crm-btn crm-btn-sm crm-btn-outline" href={`https://wa.me/91${lead.mobile}`} target="_blank" rel="noreferrer">
              <Icon name="message" size={13} /> WhatsApp
            </a>
            <a className="crm-btn crm-btn-sm crm-btn-outline" href={`mailto:${lead.email}`}>
              <Icon name="mail" size={13} /> Email
            </a>
          </div>
        </div>

        <div className="crm-panel-card">
          <div className="crm-panel-card-title">Requirement</div>
          <div className="crm-info-row"><span>Product</span><strong>{lead.product || '—'}</strong></div>
          <div className="crm-info-row"><span>Quantity</span><strong>{lead.quantity || '—'}</strong></div>
          <div className="crm-info-row"><span>Expected Value</span><strong className="crm-value-success">{money(lead.expectedValue)}</strong></div>
          <div className="crm-info-row"><span>Expected Close</span><strong>{fmt(lead.expectedClosingDate)}</strong></div>
          <div className="crm-req-text">{lead.requirement}</div>
          {lead.notes && (
            <div className="crm-notes-text"><Icon name="fileText" size={12} /> <span>{lead.notes}</span></div>
          )}
        </div>
      </div>

      {/* STATUS PIPELINE */}
      {!editing && (
        <div className="crm-panel-card">
          <div className="crm-panel-card-title">Move Stage</div>
          <div className="crm-stage-list">
            {STAGES.filter((s) => s !== 'Converted').map((s) => (
              <button key={s} className={`crm-btn crm-btn-sm ${lead.status === s ? 'crm-btn-primary' : 'crm-btn-outline'}`}
                onClick={() => setStatus(s)}>
                {lead.status === s && <Icon name="dot" size={8} />}{s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* EDIT FORM */}
      {editing && (
        <div className="crm-panel-card">
          <div className="crm-panel-card-title">Edit Lead</div>
          <div className="crm-form-grid">
            {field('customerName', 'Customer Name')}
            {field('company', 'Company')}
            {field('mobile', 'Mobile')}
            {field('email', 'Email', 'email')}
            {field('city', 'City')}
            <StateSelect value={form.state} onChange={(val) => setForm((p) => ({ ...p, state: val }))} />
            <UserSearchInput
              label="Assigned To"
              value={form.assignedTo}
              onChange={(val) => setForm((p) => ({ ...p, assignedTo: val }))}
            />
            {field('expectedValue', 'Expected Value', 'number')}
            {field('expectedClosingDate', 'Expected Close', 'date')}
            <FieldSelect
              label="Priority"
              value={form.priority}
              options={PRIORITIES}
              onChange={(val) => setForm((p) => ({ ...p, priority: val }))}
            />
            <FieldSelect
              label="Status"
              value={form.status}
              options={STAGES}
              onChange={(val) => setForm((p) => ({ ...p, status: val }))}
            />
            <div className="crm-field crm-field-full">
              <label>Notes</label>
              <textarea rows={3} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <ProductRows rows={products} setRows={setProducts} goods={goods} loading={goodsLoading} searchGoods={searchGoods} />
        </div>
      )}

      {/* TABS */}
      <div className="crm-tabs">
        {tabs.map((t) => (
          <button key={t.key} className={`crm-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
        <div className="crm-tabs-actions">
          <button className="crm-btn crm-btn-sm crm-btn-outline" onClick={() => setCallModal(true)}>
            <Icon name="phone" size={13} /> Log Call
          </button>
          <button className="crm-btn crm-btn-sm crm-btn-outline" onClick={() => setFollowupModal(true)}>
            <Icon name="calendar" size={13} /> New Follow-up
          </button>
        </div>
      </div>

      <div className="crm-panel-card">
        {tab === 'overview' && (
          <div className="crm-form-grid">
            <div className="crm-field"><label>Requirement</label><div className="crm-static">{lead.requirement}</div></div>
            <div className="crm-field"><label>Address</label><div className="crm-static">{lead.address || '—'}</div></div>
            <div className="crm-field"><label>State</label><div className="crm-static">{lead.state || '—'}</div></div>
            <div className="crm-field"><label>Alt Mobile</label><div className="crm-static">{lead.altMobile || '—'}</div></div>
            <div className="crm-field"><label>Unit Price</label><div className="crm-static">{money(lead.unitPrice)}</div></div>
            <div className="crm-field"><label>Source Detail</label><div className="crm-static">{lead.source}{lead.indiamartLeadId ? ` · ${lead.indiamartLeadId}` : ''}</div></div>
          </div>
        )}
        {tab === 'activities' && <ActivityTimeline activities={activities} />}
        {tab === 'calls' && (
          <div className="crm-table-scroll">
          <table className="crm-table">
            <thead><tr><th>Direction</th><th>Outcome</th><th>Duration</th><th>Called At</th><th>Notes</th></tr></thead>
            <tbody>
              {calls.map((c) => (
                <tr key={c.id}>
                  <td>
                    <span className="crm-cell-inline">
                      <Icon name={c.direction === 'Inbound' ? 'arrowDown' : 'arrowUp'} size={12} />
                      {c.direction === 'Inbound' ? 'Inbound' : 'Outbound'}
                    </span>
                  </td>
                  <td><LeadStatusBadge status={c.outcome === 'Interested' ? 'Interested' : c.outcome === 'Not Interested' ? 'Lost' : 'Contacted'} labelOverride={c.outcome} /></td>
                  <td>{dur(c.durationSeconds)}</td>
                  <td>{fmtDT(c.calledAt)}</td>
                  <td>{c.notes || '—'}</td>
                </tr>
              ))}
              {!calls.length && <tr><td colSpan={5} className="crm-table-empty">No calls logged yet.</td></tr>}
            </tbody>
          </table>
          </div>
        )}
        {tab === 'followups' && (
          <div className="crm-table-scroll">
          <table className="crm-table">
            <thead><tr><th>Type</th><th>Scheduled</th><th>Priority</th><th>Status</th><th>Notes</th></tr></thead>
            <tbody>
              {followups.map((f) => (
                <tr key={f.id}>
                  <td>{f.type}</td>
                  <td>{fmtDT(f.scheduledAt)}</td>
                  <td><PriorityBadge priority={f.priority} /></td>
                  <td><LeadStatusBadge status={f.status === 'Completed' ? 'Won' : 'New'} labelOverride={f.status} /></td>
                  <td>{f.notes || '—'}</td>
                </tr>
              ))}
              {!followups.length && <tr><td colSpan={5} className="crm-table-empty">No follow-ups scheduled.</td></tr>}
            </tbody>
          </table>
          </div>
        )}
        {tab === 'opportunities' && (
          <div className="crm-table-scroll">
          <table className="crm-table">
            <thead><tr><th>Opportunity</th><th>Value</th><th>Stage</th><th>Probability</th><th>Close Date</th></tr></thead>
            <tbody>
              {opps.map((o) => (
                <tr key={o.id} className="crm-row-clickable" onClick={() => navigate(`/crm/opportunities/${o.id}`)}>
                  <td className="link-cell">{o.opportunityId}</td>
                  <td className="crm-cell-strong">{money(o.expectedValue)}</td>
                  <td><LeadStatusBadge status={o.stage} /></td>
                  <td>{o.probability}%</td>
                  <td>{fmt(o.expectedCloseDate)}</td>
                </tr>
              ))}
              {!opps.length && <tr><td colSpan={5} className="crm-table-empty">No opportunities yet — use the Opportunity button above.</td></tr>}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <CallModal open={callModal} lead={lead} onClose={() => setCallModal(false)}
        onSaved={fetchRelated}
        onSave={async (f) => { try { await crmLeadApi.addCall(id, f); } catch { throw new Error('save-failed'); } toast.success('Call logged.'); setCallModal(false); }} />
      <FollowupModal open={followupModal} lead={lead} onClose={() => setFollowupModal(false)}
        onSave={async (f) => { try { await crmLeadApi.addFollowup(id, f); fetchRelated(); } catch { throw new Error('save-failed'); } toast.success('Follow-up created.'); setFollowupModal(false); }} />
    </div>
  );
};


export default function CRMLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  return id ? <LeadDetail navigate={navigate} id={id} /> : <LeadCreate navigate={navigate} />;
}
